import { createFileRoute, Link } from "@tanstack/react-router";
import { seoHead } from "@/lib/seo";
import { useState } from "react";
import { z } from "zod";
import { CheckCircle2, ArrowLeft, Star, Send } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/submit-review")({
  head: () =>
    seoHead({
      path: "/submit-review",
      title: "Write a Review — Coffman Cove Lodging",
      description:
        "Share your experience at Coffman Cove Lodging. Reviews are read by the Hedges family and published after a quick check.",
    }),
  component: SubmitReviewPage,
});

const schema = z.object({
  name: z.string().trim().min(1, "Please tell us your name").max(80),
  location: z.string().trim().max(80).optional().or(z.literal("")),
  email: z
    .string()
    .trim()
    .max(200)
    .email("Enter a valid email")
    .optional()
    .or(z.literal("")),
  title: z.string().trim().max(120).optional().or(z.literal("")),
  body: z
    .string()
    .trim()
    .min(20, "Please write at least a sentence or two")
    .max(2000),
  rating: z.number().int().min(1).max(5),
});

const inputClass =
  "w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-[var(--color-ember)]";

function SubmitReviewPage() {
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [email, setEmail] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const parsed = schema.safeParse({ name, location, email, title, body, rating });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Please check your entries.");
      return;
    }
    setSubmitting(true);
    try {
      const { error: insertErr } = await supabase.from("guest_reviews").insert({
        author_name: parsed.data.name,
        author_location: parsed.data.location || null,
        author_email: parsed.data.email || null,
        title: parsed.data.title || null,
        body: parsed.data.body,
        rating: parsed.data.rating,
        source: "Website",
        status: "pending",
      });
      if (insertErr) throw insertErr;
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-20 text-center">
        <CheckCircle2 size={56} className="mx-auto text-[var(--color-ember)]" aria-hidden />
        <h1 className="font-display text-3xl md:text-4xl mt-6">Thanks for the review!</h1>
        <p className="mt-4 text-muted-foreground">
          The Hedges family reads every review. Yours will appear on the reviews page once it has
          been approved.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link
            to="/reviews"
            className="rounded-md bg-[var(--color-ember)] px-6 py-3 text-sm font-semibold uppercase tracking-wider text-[var(--color-ember-foreground)] hover:opacity-90"
          >
            Back to reviews
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 sm:px-6 py-14 md:py-20">
      <Link
        to="/reviews"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-[var(--color-ember)]"
      >
        <ArrowLeft size={14} aria-hidden />
        Back to reviews
      </Link>

      <h1 className="font-display text-3xl md:text-4xl mt-6">Write a review</h1>
      <p className="mt-3 text-muted-foreground">
        Tell other anglers and families about your stay. Every review is moderated before it is
        published.
      </p>

      <form onSubmit={onSubmit} className="mt-8 space-y-5">
        <div>
          <span className="block text-sm font-medium mb-2">Your rating</span>
          <div className="flex gap-1" role="radiogroup" aria-label="Star rating">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                role="radio"
                aria-checked={rating === n}
                aria-label={`${n} star${n > 1 ? "s" : ""}`}
                onClick={() => setRating(n)}
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                className="p-1"
              >
                <Star
                  size={28}
                  className={
                    n <= (hover || rating)
                      ? "fill-[var(--color-ember)] text-[var(--color-ember)]"
                      : "text-muted-foreground/40"
                  }
                />
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="font-medium">Name *</span>
            <input
              className={`mt-1 ${inputClass}`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              required
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium">City &amp; state</span>
            <input
              className={`mt-1 ${inputClass}`}
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              maxLength={80}
              placeholder="Boise, ID"
            />
          </label>
        </div>

        <label className="block text-sm">
          <span className="font-medium">Email (not published)</span>
          <input
            type="email"
            className={`mt-1 ${inputClass}`}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={200}
          />
        </label>

        <label className="block text-sm">
          <span className="font-medium">Headline</span>
          <input
            className={`mt-1 ${inputClass}`}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={120}
            placeholder="Best self-guided trip we've taken"
          />
        </label>

        <label className="block text-sm">
          <span className="font-medium">Your review *</span>
          <textarea
            className={`mt-1 ${inputClass} min-h-40`}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={2000}
            required
          />
          <span className="mt-1 block text-xs text-muted-foreground">
            {body.trim().length}/2000
          </span>
        </label>

        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center gap-2 rounded-full bg-[var(--color-ember)] px-6 py-3 text-sm font-semibold uppercase tracking-wider text-[var(--color-ember-foreground)] hover:opacity-90 disabled:opacity-60"
        >
          <Send size={16} aria-hidden />
          {submitting ? "Sending…" : "Submit review"}
        </button>
      </form>
    </div>
  );
}
